import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Button,
    makeStyles,
    shorthands,
    Spinner,
    Text,
    tokens,
} from "@fluentui/react-components";
import { ArrowSync20Regular, Stop20Regular } from "@fluentui/react-icons";
import {
    telephonyService,
    type RebuildLegsStatusDto,
} from "../../services/telephonyService";

/** Solange der Import läuft, alle drei Sekunden nachfragen. */
const TAKT_MS = 3000;

const useStyles = makeStyles({
    karte: {
        ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
        ...shorthands.borderRadius("8px"),
        ...shorthands.padding("16px", "20px"),
        display: "flex",
        flexDirection: "column",
        gap: "12px",
    },
    kopf: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" },
    knoepfe: { display: "flex", gap: "8px", flexShrink: 0 },
    erklaerung: { color: tokens.colorNeutralForeground3, maxWidth: "70ch" },
    zahlen: { display: "flex", flexWrap: "wrap", gap: "20px" },
    zahl: { display: "flex", flexDirection: "column", gap: "2px" },
    zahlWert: { fontSize: "18px", fontWeight: 600 },
    zahlLabel: { fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", color: tokens.colorNeutralForeground3 },
    laeuft: { display: "flex", alignItems: "center", gap: "8px" },
    fehler: { color: tokens.colorPaletteRedForeground1 },
});

const datum = (iso?: string | null): string => {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isFinite(d.getTime()) ? d.toLocaleString("de-DE") : "—";
};

const monat = (iso?: string | null): string => {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isFinite(d.getTime())
        ? d.toLocaleDateString("de-DE", { month: "long", year: "numeric" })
        : "";
};

/**
 * Reparatur der Anruf-Historie.
 *
 * Anrufe, die vor dem Umbau entstanden sind, kennen ihre Stationen nicht: Ein
 * weitergeleiteter Anruf steht deshalb noch bei dem, bei dem es zuerst
 * klingelte, und ein verpasster Team-Anruf bei niemandem. Dieser Lauf holt die
 * Stationen von der Telefonanlage nach.
 *
 * Er dauert je nach Zeitraum viele Minuten — die Seite darf deshalb nicht auf
 * das Ende warten, sondern fragt den Fortschritt im Takt ab.
 */
export const AnrufHistorieReparieren: React.FC = () => {
    const styles = useStyles();
    const [status, setStatus] = useState<RebuildLegsStatusDto | null>(null);
    const [laden, setLaden] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const holen = useCallback(async () => {
        try {
            const s = await telephonyService.rebuildLegsStatus();
            setStatus(s);
            setFehler(null);
            return s;
        } catch (e) {
            setFehler((e as Error).message);
            return null;
        } finally {
            setLaden(false);
        }
    }, []);

    // Im Takt nachfragen, solange etwas läuft — und beim Verlassen aufräumen,
    // damit kein Timer weiterläuft.
    useEffect(() => {
        let abgemeldet = false;
        const takt = async () => {
            const s = await holen();
            if (abgemeldet) return;
            if (s?.running) timer.current = setTimeout(takt, TAKT_MS);
        };
        void takt();
        return () => {
            abgemeldet = true;
            if (timer.current) clearTimeout(timer.current);
        };
    }, [holen]);

    const starten = async () => {
        setFehler(null);
        try {
            const s = await telephonyService.startRebuildLegs();
            setStatus(s);
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(async function takt() {
                const n = await holen();
                if (n?.running) timer.current = setTimeout(takt, TAKT_MS);
            }, TAKT_MS);
        } catch (e) {
            setFehler((e as Error).message);
        }
    };

    const stoppen = async () => {
        try {
            setStatus(await telephonyService.stopRebuildLegs());
        } catch (e) {
            setFehler((e as Error).message);
        }
    };

    const laeuft = !!status?.running;

    return (
        <div className={styles.karte}>
            <div className={styles.kopf}>
                <Text size={500} weight="semibold">
                    Anruf-Historie reparieren
                </Text>
                <div className={styles.knoepfe}>
                    {laeuft && (
                        <Button icon={<Stop20Regular />} onClick={stoppen}>
                            Stoppen
                        </Button>
                    )}
                    <Button
                        appearance="primary"
                        icon={<ArrowSync20Regular />}
                        onClick={starten}
                        disabled={laeuft || laden}
                    >
                        {laeuft ? "Läuft …" : "Historie reparieren"}
                    </Button>
                </div>
            </div>

            <Text className={styles.erklaerung}>
                Holt für ältere Anrufe die Stationen nach, die sie in der Telefonanlage
                durchlaufen haben. Erst danach steht ein weitergeleiteter Anruf auch bei dem,
                der ihn angenommen hat, und ein verpasster Anruf auf ein Team bei allen
                Mitgliedern dieses Teams. Die Teams der Anlage holt der Lauf selbst, bevor er
                beginnt; ein früherer Lauf ohne sie wird dabei richtiggestellt. Er dauert je
                nach Menge viele Minuten — diese Seite kann in der Zwischenzeit verlassen
                werden.
            </Text>

            {laden && <Spinner size="tiny" label="Stand wird geladen …" />}

            {fehler && <Text className={styles.fehler}>{fehler}</Text>}

            {status && (
                <>
                    {laeuft && (
                        <div className={styles.laeuft}>
                            <Spinner size="tiny" />
                            <Text>
                                {status.schritt ?? "Läuft"}
                                {status.fensterVon ? ` · ${monat(status.fensterVon)}` : ""} · seit{" "}
                                {datum(status.startedAt)}
                            </Text>
                        </div>
                    )}
                    {!laeuft && (
                        <Text>
                            {status.abgebrochen ? "Gestoppt" : "Fertig"} am{" "}
                            {datum(status.finishedAt)} · Zeitraum {datum(status.von)} bis{" "}
                            {datum(status.bis)}
                        </Text>
                    )}
                    <div className={styles.zahlen}>
                        <Zahl wert={status.anrufeBerechnet} label="Anrufe berichtigt" />
                        <Zahl wert={status.anrufeNeu} label="davon neu angelegt" />
                        <Zahl wert={status.abschnitte} label="Stationen" />
                        <Zahl wert={status.korrigiert} label="Teambezug ergänzt" />
                        <Zahl wert={status.fehler} label="Fehler" />
                    </div>
                    {status.letzterFehler && (
                        <Text className={styles.fehler}>Zuletzt: {status.letzterFehler}</Text>
                    )}
                </>
            )}
        </div>
    );
};

const Zahl: React.FC<{ wert: number; label: string }> = ({ wert, label }) => {
    const styles = useStyles();
    return (
        <div className={styles.zahl}>
            <span className={styles.zahlWert}>{wert.toLocaleString("de-DE")}</span>
            <span className={styles.zahlLabel}>{label}</span>
        </div>
    );
};
